import { useEffect, useState } from 'react';

/**
 * Aperçu d'un email avant son envoi, pour tous les boutons « Envoyer » de
 * l'application : l'objet et le corps se relisent et se corrigent ici, et
 * rien ne part avant le clic sur « Envoyer ».
 *
 * `charger` renvoie l'aperçu préparé par le serveur :
 *   { destinataire | destinataires[], sujet, texte, piecesJointes[],
 *     gabarit?, sansEmail[] }
 * `gabarit: true` : l'email part à plusieurs destinataires, et les balises
 * {{…}} restées dans le texte seront remplies pour chacun (son nom, son
 * lien) — l'écran le dit plutôt que de montrer un faux email.
 *
 * `onEnvoyer({ sujet, texte })` fait l'envoi réel ; la modale se ferme
 * d'elle-même en cas de succès.
 */
function ApercuEmail({ titre, sousTitre, charger, onEnvoyer, onClose, libelleEnvoyer = 'Envoyer' }) {
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [apercu, setApercu] = useState(null);
  const [sujet, setSujet] = useState('');
  const [texte, setTexte] = useState('');
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  useEffect(() => {
    let vivant = true;
    setChargement(true);
    setErreur(null);
    Promise.resolve()
      .then(() => charger())
      .then((res) => {
        if (!vivant) return;
        const d = res.data || res;
        setApercu(d);
        setSujet(d.sujet || '');
        setTexte(d.texte || '');
      })
      .catch((err) => vivant && setErreur(err.message || "Impossible de préparer l'email."))
      .finally(() => vivant && setChargement(false));
    return () => {
      vivant = false;
    };
    // `charger` est recréé à chaque rendu par les appelants : on ne charge
    // qu'à l'ouverture, la modale vit le temps d'un envoi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const destinataires = apercu
    ? apercu.destinataires || (apercu.destinataire ? [apercu.destinataire] : [])
    : [];

  async function envoyer() {
    setEnvoiEnCours(true);
    setErreur(null);
    try {
      await onEnvoyer({ sujet, texte });
      onClose();
    } catch (err) {
      setErreur(err.message || 'Envoi impossible.');
      setEnvoiEnCours(false);
    }
  }

  return (
    <div
      className="devis-modal-overlay apercu-email-overlay"
      onClick={envoiEnCours ? undefined : onClose}
    >
      <div
        className="devis-modal"
        style={{ maxWidth: 680 }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="devis-modal-header">
          <div>
            <div className="devis-modal-numero">{titre || "Aperçu de l'email"}</div>
            {sousTitre && <div className="devis-modal-sub">{sousTitre}</div>}
          </div>
          <div className="devis-modal-header-right">
            <button
              type="button"
              className="devis-drawer-close"
              onClick={onClose}
              disabled={envoiEnCours}
              aria-label="Fermer"
            >
              ×
            </button>
          </div>
        </div>

        <div className="devis-modal-body">
          {chargement && <div className="devis-drawer-empty">Préparation de l'email...</div>}
          {erreur && <div className="devis-alert devis-alert-erreur">{erreur}</div>}

          {!chargement && apercu && (
            <>
              <div className="devis-form-field" style={{ marginBottom: 12 }}>
                <label>Destinataire{destinataires.length > 1 ? 's' : ''}</label>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    padding: '6px 0',
                  }}
                >
                  {destinataires.length ? (
                    destinataires.map((d) => (
                      <span key={d} className="devis-status-pill">
                        {d}
                      </span>
                    ))
                  ) : (
                    <span className="devis-status-detail">aucun destinataire</span>
                  )}
                </div>
                {apercu.sansEmail?.length > 0 && (
                  <div className="devis-status-detail">
                    Sans adresse email : {apercu.sansEmail.join(', ')}
                  </div>
                )}
              </div>

              {apercu.gabarit && (
                <div className="devis-alert" style={{ marginBottom: 12 }}>
                  Cet email part à plusieurs destinataires : les balises{' '}
                  <code>{'{{…}}'}</code> (nom, lien personnel) seront remplies
                  automatiquement pour chacun. Vous pouvez modifier le texte autour.
                </div>
              )}

              <div className="devis-form-field" style={{ marginBottom: 12 }}>
                <label htmlFor="apercu-email-sujet">Objet</label>
                <input
                  id="apercu-email-sujet"
                  type="text"
                  value={sujet}
                  onChange={(e) => setSujet(e.target.value)}
                  disabled={envoiEnCours}
                  style={{ width: '100%', boxSizing: 'border-box' }}
                />
              </div>

              <div className="devis-form-field" style={{ marginBottom: 12 }}>
                <label htmlFor="apercu-email-texte">Message</label>
                <textarea
                  id="apercu-email-texte"
                  value={texte}
                  onChange={(e) => setTexte(e.target.value)}
                  disabled={envoiEnCours}
                  rows={Math.min(18, Math.max(8, texte.split('\n').length + 2))}
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    lineHeight: 1.5,
                  }}
                />
                <div className="devis-status-detail">
                  Les liens deviennent des boutons dans l'email reçu. La modification ne vaut
                  que pour cet envoi — le modèle se change dans « Paramétrage email ».
                </div>
              </div>

              {apercu.piecesJointes?.length > 0 && (
                <div className="devis-form-field">
                  <label>Pièces jointes</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '6px 0' }}>
                    {apercu.piecesJointes.map((p) => (
                      <span key={p} className="devis-status-pill">
                        📎 {p}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="devis-modal-footer">
          <button type="button" className="devis-btn" onClick={onClose} disabled={envoiEnCours}>
            Annuler
          </button>
          <button
            type="button"
            className="devis-btn devis-btn-primary"
            onClick={envoyer}
            disabled={envoiEnCours || chargement || !apercu}
          >
            {envoiEnCours ? 'Envoi…' : libelleEnvoyer}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ApercuEmail;
